import React from 'react';
import {
	Container,
	Header,
	KeyValuePairs,
} from '@amzn/awsui-components-console';
import { ReviewableValue } from './@types/GenericForm.types';
import { formatDate } from 'src/utils/CommonUtils';

interface ReviewItemProps {
	label: string;
	value: ReviewableValue;
	formatter?: (value: ReviewableValue) => string;
}

const getValueType = (
	value: ReviewableValue,
	hasFormatter: boolean
): string => {
	if (hasFormatter) return 'formatter';
	if (value === undefined || value === null || value === '') return 'empty';
	if (Array.isArray(value)) return 'array';
	if (value instanceof Date) return 'date';

	return typeof value;
};

const formatValue = (
	value: ReviewableValue,
	formatter?: (value: ReviewableValue) => string
): string => {
	switch (getValueType(value, !!formatter)) {
	case 'formatter':
		return formatter!(value);
	case 'empty':
		return 'Not specified';
	case 'array':
		return Array.isArray(value) && value.length === 0
			? 'No items'
			: (value as Array<string | number | boolean>).join(', ');
	case 'date':
		return formatDate(value as Date);
	case 'boolean':
		return value ? 'Yes' : 'No';
	case 'string':
	case 'number':
		return String(value);
	case 'object':
		return JSON.stringify(value, null, 2);
	default:
		return String(value);
	}
};

interface ReviewSectionProps {
	title: string;
	items: ReviewItemProps[];
}

const ReviewSection: React.FC<ReviewSectionProps> = ({ title, items }) => {
	const keyValueItems = items.map((item) => ({
		label: item.label,
		value: formatValue(item.value, item.formatter),
	}));

	return (
		<Container header={<Header variant="h3">{title}</Header>}>
			<KeyValuePairs columns={3} items={keyValueItems} />
		</Container>
	);
};

export { ReviewSection };
export type { ReviewItemProps, ReviewSectionProps };
